/**
 * Onthouden (ADR 0027): short things to remember, with or without a reminder.
 * Quick add on top (`@morgen 9u` sets a reminder), then the items with a
 * reminder (next first), the pinned ones, and the rest.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlarmClock, AlarmClockOff, Pin, PinOff, Plus, Repeat, Search, Trash2 } from 'lucide-react';
import { localTime } from '@nexus/core';
import {
  createMemo,
  memoFromCapture,
  MEMO_DEFAULT_TIME,
  setMemoPinned,
  setMemoRecurrence,
  setMemoReminder,
  type Memo,
} from '@nexus/workspace';
import { useStore } from '../db/context';
import { useMemos } from './data';
import { useUi } from '../state/ui';
import { cn } from '../lib/cn';
import { todayIso } from '../components/tasks/format';
import { ModuleIcon } from '../components/ModuleIcon';

const RULES = ['', 'elke dag', 'elke week', 'elke maand', 'elke jaar'] as const;

const iconButton =
  'grid size-8 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg';

const nowMs = () => new Date().getTime();

const at = (remindAt: string) => localTime(remindAt.slice(0, 10), remindAt.slice(11, 16));

export function MemosView() {
  const { t } = useTranslation();
  const store = useStore();
  const memos = useMemos();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');

  const preview = text.trim() ? memoFromCapture(text, todayIso()) : null;
  const add = async () => {
    if (!preview) return;
    await createMemo(store, preview);
    setText('');
  };

  const q = query.trim().toLowerCase();
  const shown = (memos ?? []).filter((m) => !q || m.title.toLowerCase().includes(q));
  const withReminder = shown
    .filter((m) => m.remindAt)
    .sort((a, b) => a.remindAt!.localeCompare(b.remindAt!));
  const pinned = shown.filter((m) => !m.remindAt && m.pinned);
  const rest = shown
    .filter((m) => !m.remindAt && !m.pinned)
    .sort((a, b) => b.updatedAt - a.updatedAt);

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10" data-testid="memos">
      <header className="mb-5">
        <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
          <ModuleIcon kind="memos" size="lg" />
          {t('memos.title')}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">{t('memos.intro')}</p>
      </header>

      <form
        className="mb-2 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('memos.placeholder')}
          aria-label={t('memos.add')}
          className="min-w-0 flex-1 rounded-lg border border-border bg-bg px-3 py-2 text-sm outline-none focus:border-accent"
        />
        <button
          type="submit"
          disabled={!preview}
          className="flex items-center gap-1 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg disabled:opacity-40"
        >
          <Plus className="size-4" />
          {t('memos.addButton')}
        </button>
      </form>
      <p className="mb-6 min-h-5 text-xs text-fg-muted" aria-live="polite">
        {preview?.remindAt ? (
          <>
            <AlarmClock className="mr-1 inline size-3.5" />
            {t('memos.willRemind', { when: formatWhen(preview.remindAt, t, i18nLang()) })}
          </>
        ) : (
          t('memos.help')
        )}
      </p>

      {(memos?.length ?? 0) > 5 && (
        <label className="mb-4 flex items-center gap-2 rounded-lg border border-border px-2 py-1.5 text-sm">
          <Search className="size-4 text-fg-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('memos.search')}
            aria-label={t('memos.search')}
            className="min-w-0 flex-1 bg-transparent outline-none"
          />
        </label>
      )}

      {memos && memos.length === 0 && <p className="text-sm text-fg-muted">{t('memos.empty')}</p>}
      <Group title={t('memos.withReminder')} items={withReminder} />
      <Group title={t('memos.pinned')} items={pinned} />
      <Group title={t('memos.other')} items={rest} />
    </div>
  );
}

const i18nLang = () => document.documentElement.lang || 'nl';

function formatWhen(remindAt: string, t: (k: string) => string, language: string): string {
  const d = new Date(at(remindAt));
  const today = todayIso();
  const day =
    remindAt.slice(0, 10) === today
      ? t('memos.today')
      : d.toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day} ${remindAt.slice(11, 16)}`;
}

function Group({ title, items }: { title: string; items: Memo[] }) {
  if (!items.length) return null;
  return (
    <section className="mb-6" aria-label={title}>
      <h2 className="mb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">{title}</h2>
      <ul className="divide-y divide-border rounded-xl border border-border">
        {items.map((m) => (
          <MemoRow key={m.id} memo={m} />
        ))}
      </ul>
    </section>
  );
}

function MemoRow({ memo }: { memo: Memo }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const [editing, setEditing] = useState(false);
  const past = !!memo.remindAt && at(memo.remindAt) < nowMs();

  return (
    <li className="group flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2" data-testid="memo">
      <a
        href={`#/p/${memo.id}`}
        onClick={(e) => {
          e.preventDefault();
          open({ kind: 'page', pageId: memo.id }, e.ctrlKey || e.metaKey);
        }}
        className="min-w-0 flex-1 text-sm hover:underline"
      >
        {memo.title || t('page.untitled')}
      </a>
      {memo.remindAt && !editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={cn(
            'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs',
            past ? 'text-fg-muted line-through' : 'bg-accent/15 text-fg',
          )}
          title={t('memos.changeReminder')}
        >
          <AlarmClock className="size-3" />
          {formatWhen(memo.remindAt, t, i18n.language)}
          {memo.recurrence && <Repeat className="size-3" aria-label={memo.recurrence} />}
        </button>
      )}
      {editing && (
        <span className="flex flex-wrap items-center gap-1">
          <input
            type="datetime-local"
            aria-label={t('memos.reminderAt')}
            defaultValue={memo.remindAt ?? `${todayIso()}T${MEMO_DEFAULT_TIME}`}
            onChange={(e) => e.target.value && void setMemoReminder(store, memo.id, e.target.value)}
            className="rounded-md border border-border bg-bg px-1 py-0.5 text-xs"
          />
          <select
            aria-label={t('memos.repeat')}
            value={memo.recurrence ?? ''}
            onChange={(e) => void setMemoRecurrence(store, memo.id, e.target.value || null)}
            className="rounded-md border border-border bg-bg px-1 py-0.5 text-xs"
          >
            {RULES.map((r) => (
              <option key={r} value={r}>
                {r ? t(`memos.rules.${r.split(' ')[1]}`) : t('memos.noRepeat')}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded px-2 py-0.5 text-xs hover:bg-bg-hover"
          >
            {t('memos.done')}
          </button>
        </span>
      )}
      <span className="flex items-center [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-within:opacity-100">
        {memo.remindAt ? (
          <button
            type="button"
            className={iconButton}
            aria-label={t('memos.removeReminder')}
            title={t('memos.removeReminder')}
            onClick={() => {
              setEditing(false);
              void setMemoReminder(store, memo.id, null);
            }}
          >
            <AlarmClockOff className="size-4" />
          </button>
        ) : (
          <button
            type="button"
            className={iconButton}
            aria-label={t('memos.addReminder')}
            title={t('memos.addReminder')}
            onClick={() => setEditing(true)}
          >
            <AlarmClock className="size-4" />
          </button>
        )}
        <button
          type="button"
          className={iconButton}
          aria-label={memo.pinned ? t('memos.unpin') : t('memos.pin')}
          title={memo.pinned ? t('memos.unpin') : t('memos.pin')}
          onClick={() => void setMemoPinned(store, memo.id, !memo.pinned)}
        >
          {memo.pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
        </button>
        <button
          type="button"
          className={iconButton}
          aria-label={t('memos.delete')}
          title={t('memos.delete')}
          onClick={() => void store.trashPage(memo.id)}
        >
          <Trash2 className="size-4" />
        </button>
      </span>
    </li>
  );
}
